import { ReactElement, useMemo, useState } from 'react'
import FilterValueProps from 'types/FilterValueProps'
import { Button, Form, Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import { IonIcon } from '@ionic/react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import "react-datepicker/dist/react-datepicker.css"
import { checkboxOutline, checkmark, squareOutline } from 'ionicons/icons'
import useFilterOptions from './hooks/useFilterOptions'
import useSelectedOptions from './hooks/useSelectedOptions'
import useInit from './hooks/useInit'
import useParentFilter from './hooks/useParentFilter'
import setDefaultOption from './utils/setDefaultOption'
import truncate from 'pages/ReportPage/utils/truncate'
import useToggleSelectAll from './hooks/useToggleSelectAll'
import useSetDefaultOption from './hooks/useSetDefaultOption'
import useSetChildFilterError from './hooks/useSetChildFilterError'
import ChildFilterErrors from 'types/ChildFilterErrors'

interface ReportFilterProps {
  id: number
  name: string
  param: string
  setFilterParam: Function
  parentFilterParams?: any
  parentFilterId?: number
  isLoaded?: Function
  isMulti: boolean
  displaySelectAll: boolean
  selectAllIsDefault: boolean
  selectAllOptionsLabel?: string
  selectAllOptionsValue?: string
  setFilterChangeTime?: Function
  setChildFilterErrors: (callback: (prev: ChildFilterErrors) => ChildFilterErrors) => void
  childFilterPageErrorMessage?: string | null
}

const ReportFilter: React.FunctionComponent<ReportFilterProps> = (props: ReportFilterProps) => {

  const [allOptions, setAllOptions] = useState<FilterValueProps[]>([])
  const [parentFilterParams, setParentFilterParams] = useState<FilterValueProps[]>([])
  const [options, setOptions] = useState<FilterValueProps[]>([])
  const [filteredOptions, setFilteredOptions] = useState<FilterValueProps[]>([])
  const [showDatetime, setShowDatetime] = useState<boolean>(false)
  const [showDate, setShowDate] = useState<boolean>(false)
  const [hasDatetime, setHasDatetime] = useState<boolean>(false)
  const [hasDate, setHasDate] = useState<boolean>(false)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(null)
  const [formattedStartDate, setFormattedStartDate] = useState<string | false>(false)
  const [formattedEndDate, setFormattedEndDate] = useState<string | false>(false)
  const [startTime, setStartTime] = useState<string>('00:00')
  const [endTime, setEndTime] = useState<string>('00:00')
  const [labelText, setLabelText] = useState<string>(props.name)
  const [parentFilterParam, setParentFilterParam] = useState<string | false>(false)
  const [parentFilterName, setParentFilterName] = useState<string | false>(false)
  const [parentValue, setParentValue] = useState<string | false>(false)
  const [datetimeParam, setDatetimeParam] = useState<string | false>(false)
  const [dateParam, setDateParam] = useState<string | false>(false)
  const [wasLoaded, setWasLoaded] = useState<boolean>(false)
  const [selectedOptions, setSelectedOptions] = useState<Array<FilterValueProps>>([])
  const [currDateOption, setCurrDateOption] = useState<FilterValueProps | null>(null)
  const [currDateTimeOption, setCurrDateTimeOption] = useState<FilterValueProps | null>(null)
  const [filterText, setFilterText] = useState<string>('')
  const [allItemsSelected, setAllItemsSelected] = useState<boolean>(false)

  const datetimesLabel: string = 'Custom Datetimes'
  
  const isFilterScrollable: boolean = useMemo(() => options.length > 10, [options]);
  const isChildFilterEmpty: boolean = useMemo(() => parentFilterParam !== false && options.length < 1, [options, parentFilterParam]);

  const onSelectValue = (param: string, value: any, label: string) => {
    props.setFilterParam(param, value)
    setLabelText(label)

    if (props.setFilterChangeTime) {
      props.setFilterChangeTime(new Date().toISOString())
    }
  }

  const onToggleOption = (option: FilterValueProps) => {
    if (props.isMulti) {
      setSelectedOptions((orig) => {
        let multiSelectsOnly = [...orig]

        //remove or add option
        if (orig.includes(option)) {
          multiSelectsOnly = multiSelectsOnly.filter(o => o !== option)
        } else {
          multiSelectsOnly.push(option)
        }

        return multiSelectsOnly;
      })
    }
  }

  const onSelectOption = (param: string, option: FilterValueProps, propagateEvent: boolean = true) => {
    if (props.isMulti) {
      setSelectedOptions((orig) => {
        let multiSelectsOnly = [...orig]

        if (!orig.includes(option)) {
          multiSelectsOnly.push(option)
        }

        return multiSelectsOnly;
      })
    } else {
      setSelectedOptions([option])
      if (propagateEvent) {
        onSelectValue(param, option.value, option.label)
      }
    }
  }

  const selectAll = () => {
    setSelectedOptions(options)
  }

  const unselectAll = () => {
    setSelectedOptions([])
  }

  useFilterOptions({
    options,
    setFilteredOptions,
    filterText
  })

  useSelectedOptions({
    options,
    selectedOptions,
    onSelectValue,
    name: props.name,
    param: props.param,
    isMulti: props.isMulti,
    selectAllOptionsLabel: props.selectAllOptionsLabel,
    selectAllOptionsValue: props.selectAllOptionsValue
  })

  useInit({
    id: props.id,
    parentFilterId: props.parentFilterId,
    param: props.param,
    setAllOptions,
    setDefaultOption,
    setOptions,
    setDatetimeParam,
    setDateParam,
    setParentFilterParam: setParentFilterParam,
    setParentFilterName: setParentFilterName,
    setFilterParam: props.setFilterParam,
    isLoaded: props.isLoaded,
    isMulti: props.isMulti,
    selectAllIsDefault: props.selectAllIsDefault,
    setLabelText,
    onSelectOption,
    selectAll,
    setHasDate,
    setHasDatetime
  })

  useSetDefaultOption({
    parentFilterId: props.parentFilterId,
    allOptions,
    options,
    setDefaultOption,
    onSelectOption,
    setFilterParam: props.setFilterParam,
    isMulti: props.isMulti,
    selectAllIsDefault: props.selectAllIsDefault,
    param: props.param,
    selectAll: selectAll
  })

  useParentFilter({
    parentFilterParentParams: props.parentFilterParams,
    parentFilterParams,
    setParentFilterParams,
    setParentValue,
    setOptions,
    parentFilterParam: parentFilterParam,
    parentValue,
    allOptions,
    setLabelText,
    setDefaultOption,
    setFilterParam: props.setFilterParam,
    param: props.param,
    wasLoaded,
    setWasLoaded,
    isLoaded: props.isLoaded,
    unselectAll,
  })

  useToggleSelectAll({
    options,
    selectedOptions,
    setAllItemsSelected
  })

  useSetChildFilterError({
    param: props.param,
    name: props.name,
    parentFilterName: parentFilterName,
    isChildFilterEmpty,
    setChildFilterErrors: props.setChildFilterErrors,
    wasLoaded: wasLoaded,
  })

  const setTime = () => {
    const start = formattedStartDate + ' ' + startTime
    const end = formattedEndDate + ' ' + endTime

    onSelectOption(props.param, currDateTimeOption!, false)
    onSelectValue(props.param, start + ',' + end, datetimesLabel)
    return
  }

  const handleItemClick = (e, option, callback) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
  };

  const renderDropdownItem = (option: FilterValueProps, index: number) => {
    const labelPrefix: ReactElement | undefined = (selectedOptions.includes(option) ? (<><IonIcon icon={props.isMulti ? checkboxOutline : checkmark} className={'ionicon text-' + ((props.displaySelectAll && allItemsSelected) ? 'secondary' : 'primary')} />&nbsp;</>) : props.isMulti ? (<><IonIcon icon={squareOutline} className='ionicon text-secondary' />&nbsp;</>) : undefined)
    const labelTruncateTo = (typeof labelPrefix === 'undefined') ? 30 : 26;
    const label: ReactElement = <>{labelPrefix}{truncate(option.label, labelTruncateTo)}</>;
    
    switch (option.type) {
      case 'dt':
        return (
          <Dropdown.Item
            key={index}
            eventKey={index}
            onClick={(e) => handleItemClick(e, option, () => {
              setCurrDateTimeOption(option);
              setShowDatetime(true);
            })}
          >
            {label}
          </Dropdown.Item>
        );

      case 'd':
        return (
          <Dropdown.Item
            key={index}
            eventKey={index}
            onClick={(e) => handleItemClick(e, option, () => {
              setCurrDateOption(option);
              setShowDate(true);
            })}
          >
            {label}
          </Dropdown.Item>
        );

      default:
        return props.isMulti ? (<Dropdown.Item
          key={index}
          eventKey={index}
          disabled={props.displaySelectAll && allItemsSelected}
          aria-readonly={props.displaySelectAll && allItemsSelected}
          onClick={(e) => {
            e.stopPropagation();
            onToggleOption(option);
          }}
        >{label}</Dropdown.Item>)
          : (
            <Dropdown.Item
              key={index}
              eventKey={index}
              onClick={() => onSelectOption(props.param, option)}
            >
              {label}
            </Dropdown.Item>
          );
    }
  };


  const renderFilterContent = () => {
    return <>
      <Dropdown.Header>{props.name}</Dropdown.Header>
      {
        (isFilterScrollable) && (<div className='col-10 offset-1'><Form.Control type='text' className='mb-1' placeholder='Filter values...' onChange={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setFilterText(e.currentTarget.value!)
        }} /></div>)
      }
      {
        props.isMulti && props.displaySelectAll && options.length > 0 && (
          <Button
            variant='primary'
            size='sm'
            disabled={!allItemsSelected && options?.length < 1}
            className='mb-1 col-10 offset-1'
            onClick={(e) => {
              allItemsSelected ? unselectAll() : selectAll()
            }}
          >
            {allItemsSelected ? 'Unselect All' : 'Select all'}
          </Button>
        )
      }
      {
        filteredOptions.map((option: FilterValueProps, index: number) => {
          return renderDropdownItem(option, index)
        })
      }
    </>
  }

  return (
    <Dropdown
      className='filter'
    >
      <Dropdown.Toggle id="dropdown-basic" className={isChildFilterEmpty ? 'text-danger' : ''}>
        {truncate(labelText)}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <div className='filter-inner'>
          {hasDatetime && !showDate && <div className={'datetime-picker datetime' + (showDatetime ? ' show' : '')}>
            <a onClick={() => { setShowDatetime(false) }}>Back</a>
            {datetimeParam && <>
              <DatePicker
                dateFormat="MM-dd-yyyy"
                selected={startDate}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setStartDate(start)
                  setEndDate(end)

                  if (start === null || end === null) {
                    return
                  }

                  // set value
                  const fStartDate = (start.getFullYear() + '-' + (start.getMonth() + 1) +
                    '-' + start.getDate()).replace(/\d+/g, c => c.padStart(2, '0'))
                  setFormattedStartDate(fStartDate)

                  const fEndDate = (end.getFullYear() + '-' + (end.getMonth() + 1) +
                    '-' + end.getDate()).replace(/\d+/g, c => c.padStart(2, '0'))
                  setFormattedEndDate(fEndDate)

                  onSelectOption(props.param, currDateTimeOption!, false)

                  if (startTime && endTime) {
                    const start = fStartDate + ' ' + startTime
                    const end = fEndDate + ' ' + endTime

                    onSelectOption(props.param, currDateTimeOption!, false)
                    onSelectValue(props.param, start + ',' + end, datetimesLabel)
                  }
                  else {
                    onSelectValue(props.param, fStartDate + ',' + fEndDate, datetimesLabel)
                  }
                }}
                renderDayContents={(day, date) => {
                  return <div className='cont'>{day}</div>
                }}

                startDate={startDate}
                endDate={endDate}
                inline
                selectsRange
              />
              <div className='time-select-wrapper'>
                <label>Time From</label>
                <Form.Control type='time' name='timeFrom' defaultValue="00:00" onChange={(e) => setStartTime(e.target.value)} />
                <label>Time To</label>
                <Form.Control type='time' name='timeTo' defaultValue="00:00" onChange={(e) => setEndTime(e.target.value)} />
                <Button onClick={() => setTime()}>Set Time</Button>
              </div>
            </>}
          </div>}
          {hasDate && !showDatetime && <div className={'datetime-picker date ' + (showDate ? ' show' : '')}>
            <a onClick={() => { setShowDate(false) }}>Back</a>
            {dateParam && <>
              <DatePicker
                dateFormat="MM-dd-yyyy"
                selected={startDate}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setStartDate(start)
                  setEndDate(end)

                  if (start === null || end === null) {
                    return
                  }

                  // set value
                  const fStartDate = start.getFullYear() + '-' + (start.getMonth() + 1) +
                    '-' + start.getDate()

                  const fEndDate = end.getFullYear() + '-' + (end.getMonth() + 1) +
                    '-' + end.getDate()

                  onSelectOption(props.param, currDateOption!, false)
                  onSelectValue(props.param, fStartDate.replace(/\d+/g, c => c.padStart(2, '0')) + ',' +
                    fEndDate.replace(/\d+/g, c => c.padStart(2, '0')), 'Custom Dates')
                }}
                renderDayContents={(day, date) => {
                  return <div className='cont'>{day}</div>
                }}
                startDate={startDate}
                endDate={endDate}
                inline
                selectsRange
              />
            </>}
          </div>}
          <div className={'filter-options ' + ((isFilterScrollable) ? 'scrollable ' : ' ') + 
            (props.isMulti ? 'with-selectall' : 'no-selectall') + (showDate ? ' no-show' : '')} >
            <div className='filter-options-inner'>
              {isFilterScrollable ? <PerfectScrollbar>
                {renderFilterContent()}
              </PerfectScrollbar> : <>{renderFilterContent()}</>}
            </div>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default ReportFilter